import { Plus } from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useNavigate } from 'react-router';
import { CREATE_GROUPS, createHref, useCreateActions, type CreateAction, type CreateContext } from '@/app/create-actions';
import { Button, Combobox, Dialog, Field, Menu, MenuItem, MenuLabel } from '@/components/ui';

/**
 * P2-0: runs a CREATE_ACTIONS entry. It navigates to the action's URL, or, when the record lives
 * in an organization and the context has none, asks for the organization first.
 *
 * `const { launch, element } = useCreateLauncher(); … launch(action, context); … {element}`
 */
export function useCreateLauncher() {
  const navigate = useNavigate();
  const [pending, setPending] = useState<{ action: CreateAction; ctx: CreateContext } | null>(null);
  const launch = (action: CreateAction, ctx: CreateContext = {}) => {
    const href = createHref(action, ctx);
    if (href) void navigate(href);
    else setPending({ action, ctx });
  };
  const element = (
    <OrganizationPicker
      action={pending?.action ?? null}
      onClose={() => setPending(null)}
      onPick={(organizationId) => {
        if (!pending) return;
        setPending(null);
        void navigate(pending.action.href({ ...pending.ctx, organizationId }));
      }}
    />
  );
  return { launch, element };
}

function OrganizationPicker({ action, onClose, onPick }: { action: CreateAction | null; onClose: () => void; onPick: (organizationId: string) => void }) {
  const [organizationId, setOrganizationId] = useState<string | null>(null);
  // Start empty each time the picker opens (adjusting state during render, not in an effect).
  const [shownFor, setShownFor] = useState(action);
  if (shownFor !== action) {
    setShownFor(action);
    setOrganizationId(null);
  }
  return (
    <Dialog
      open={Boolean(action)}
      onOpenChange={(o) => !o && onClose()}
      size="sm"
      title={action ? `New ${action.label.toLowerCase()}` : ''}
      description="Choose the organization it belongs to."
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" disabled={!organizationId} onClick={() => organizationId && onPick(organizationId)}>
            Continue
          </Button>
        </>
      }
    >
      <Field label="Organization">
        <Combobox
          value={organizationId}
          onChange={setOrganizationId}
          endpoint="/organizations"
          toOption={(x: { id: string; name: string }) => ({ value: x.id, label: x.name }) as never}
          placeholder="Search organizations…"
        />
      </Field>
    </Dialog>
  );
}

/**
 * A New ▾ button over CREATE_ACTIONS. The top bar shows every action the user may run, in the
 * section 7.2 groups. Record pages pass the actions they offer and what they know:
 * `<NewMenu keys={['ticket', 'contact', 'configuration']} context={{ organizationId: org.id }} />`.
 * Renders nothing when the user may run none of them. `children` adds the page's own items after
 * the registry's (P2-1: "Clone this configuration", "Add to monitoring").
 */
export function NewMenu({ keys, context = {}, trigger, grouped = !keys, children }: { keys?: string[]; context?: CreateContext; trigger?: ReactNode; grouped?: boolean; children?: ReactNode }) {
  const actions = useCreateActions(keys);
  const { launch, element } = useCreateLauncher();
  if (!actions.length && !children) return null;
  const item = (a: CreateAction) => (
    <MenuItem key={a.key} icon={<a.icon />} onSelect={() => launch(a, context)}>
      {a.label}
      {a.needsOrganization && !context.organizationId ? <span className="ml-auto pl-3 text-xs text-subtle">…</span> : null}
    </MenuItem>
  );
  const groups = CREATE_GROUPS.map((g) => ({ g, list: actions.filter((a) => a.group === g) })).filter((x) => x.list.length);
  return (
    <>
      <Menu
        trigger={
          trigger ?? (
            <Button variant="primary" size="md" aria-label="Create">
              <Plus />
              <span className="hidden sm:inline">New</span>
            </Button>
          )
        }
      >
        {grouped ? (
          // Wide screens: the groups flow into three columns, so the whole menu fits on screen.
          <div className="sm:w-[min(44rem,calc(100vw-2rem))] sm:columns-3 sm:gap-2">
            {groups.map(({ g, list }) => (
              <div key={g} className="break-inside-avoid pb-2">
                <MenuLabel>{g}</MenuLabel>
                {list.map(item)}
              </div>
            ))}
          </div>
        ) : (
          actions.map(item)
        )}
        {children}
      </Menu>
      {element}
    </>
  );
}
